import { useCallback, useEffect, useRef, useState } from "react";
import {
  fetchPolicyCheckTasks,
  type PolicyCheckTaskListItem,
} from "../api/policyCheck";
import { getApiErrorMessage } from "../utils/getApiErrorMessage";

interface UsePolicyCheckTasksOptions {
  id: string | undefined;
  page: number;
  pageSize: number;
  enabled?: boolean;
}

export const usePolicyCheckTasks = ({
  id,
  page,
  pageSize,
  enabled = true,
}: UsePolicyCheckTasksOptions) => {
  const [data, setData] = useState<PolicyCheckTaskListItem[]>([]);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(Boolean(enabled && id));
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);
  const silentRefetchRef = useRef(false);
  const requestKeyRef = useRef(`${id ?? ""}:${page}:${pageSize}`);

  const refetch = useCallback((options?: { silent?: boolean }) => {
    silentRefetchRef.current = options?.silent === true;
    setReloadToken((token) => token + 1);
  }, []);

  useEffect(() => {
    if (!enabled || !id) {
      setLoading(false);
      return;
    }

    const requestKey = `${id}:${page}:${pageSize}`;
    const isSameRequest = requestKeyRef.current === requestKey;
    const silent = silentRefetchRef.current && isSameRequest;
    silentRefetchRef.current = false;
    requestKeyRef.current = requestKey;

    let cancelled = false;
    if (!silent) {
      setLoading(true);
      setError(null);
    }

    fetchPolicyCheckTasks(id, { page, pageSize, sorting: "desc" })
      .then((response) => {
        if (!cancelled) {
          setData(response.items);
          setTotalPages(response.totalPages);
          if (silent) setError(null);
        }
      })
      .catch((err: unknown) => {
        if (!cancelled && !silent) {
          setError(getApiErrorMessage(err, "Не удалось загрузить файлы проверки"));
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [id, page, pageSize, enabled, reloadToken]);

  return { data, totalPages, loading, error, refetch };
};
